import {Component, ViewChild} from '@angular/core';
import {
  Events,
  ModalController,
  NavController,
  NavParams,
  Platform,
  PopoverController,
  Refresher,
  Tabs
} from 'ionic-angular';
import {CancelModalPage} from "../cancel-modal/cancel-modal";
import {HttpClient} from "@angular/common/http";
import {ApiErrorHandlerProvider} from "../../providers/api-error-handler/api-error-handler";
import moment from "moment";
import {Subscription} from "rxjs";
import {interval} from "rxjs/observable/interval";
import {takeWhile} from "rxjs/operators";
import {TabsServiceProvider} from "../../providers/tabs-service/tabs-service";
import {BookingServiceProvider} from "../../providers/booking-service/booking-service";
import {TeamServiceProvider} from "../../providers/team-service/team-service";

@Component({
    selector: 'page-bookings',
    templateUrl: 'bookings.html'
})
export class BookingsPage {

    errors: {};
    loading: boolean;
    tuid: string;
    bookings: Array<any>;
    upcomingBookings: Array<any>;
    completedBookings: Array<any>;
    currentOrNextBooking: any;

    timeInSeconds: number = 0;
    timeInMinutes: number = 0;
    source: any;
    subscribe: Subscription = new Subscription();
    pause:Subscription = new Subscription();
    resume:Subscription = new Subscription();
    pausedTime:any;

    @ViewChild(Refresher) refresher: Refresher;

    //@todo need to fetch actual images for all cards

    constructor(
        public platform: Platform,
        public navCtrl: NavController,
        public navParams: NavParams,
        public modalCtrl: ModalController,
        public popoverCtrl: PopoverController,
        public events: Events,
        private http: HttpClient,
        private teamProvider: TeamServiceProvider,
        private apiErrorHandler: ApiErrorHandlerProvider,
        private tabsService: TabsServiceProvider,
        public bookingService: BookingServiceProvider
    ) {
        this.tuid = localStorage.getItem('tuid');
        this.bookings = null;
        this.upcomingBookings = [];
        this.completedBookings = [];
        this.currentOrNextBooking = null;

        this.events.subscribe('booking:cancelled', booking=>{
          this.loading = true;
          this.getBookings().then(()=>{
            this.loading = false;
          }).catch(()=>{
            this.loading = false;
          });
        });

    }

    ngOnDestroy(){
      this.events.unsubscribe('booking:cancelled');
    }

    ionViewWillEnter() {

        this.tuid = localStorage.getItem('tuid');
        this.loading = true;
        this.getBookings().then(()=>{
            this.loading = false;

            // Start the Countdown timer
            this.timer();
        }).catch(()=>{
            this.loading = false;
        });

        // Refresh the timer when the user switches between apps
        this.timerRefreshOnDevice();

        this.tabsService.show();

    }

    ionViewWillLeave() {
        this.pause.unsubscribe();
        this.resume.unsubscribe();
        this.subscribe.unsubscribe();
    }

    timer() {
        this.source = interval(1000).pipe(
            takeWhile(() => {
                if (this.timeInSeconds > 0) {
                    return true
                }
            })
        );

        this.subscribe = this.source
            .subscribe(() => {
                this.timeInSeconds = this.timeInSeconds - 1;
                this.setTimeInMinutes(this.timeInSeconds);
            });
    }

    timerRefreshOnDevice() {
        this.platform.ready().then(() => {
            this.pause = this.platform.pause.subscribe(() => {
                this.pausedTime = moment();
            });

            this.resume = this.platform.resume.subscribe(() => {
                if (this.timeInSeconds > 0) {
                    this.loading = true;
                    this.getBookingTimer().then((booking:any) => {
                        if (booking && booking > 0) {
                            this.timeInSeconds = booking;
                            let duration = moment.duration(moment().diff(this.pausedTime)).asSeconds();
                            let seconds = booking - duration;
                            this.setTimeInMinutes(seconds);
                            this.loading = false;
                        }
                    });
                }
            });
        });
    }

    cancelBooking(booking) {
      this.bookingService.cancelBooking(booking);
    }

    getBookings() {

        return new Promise((resolve, reject) => {
            let url = `/bookings/${this.tuid}`;

            if (localStorage.getItem('multiShop') && localStorage.getItem('ownerId')) {
                url += `?owner=${localStorage.getItem('ownerId')}`
            }

            this.http.get(url)
                .subscribe((bookings: any) => {
                    this.bookings = bookings;
                    return resolve();
                }, error => {
                    this.errors = this.apiErrorHandler.handleError(error);
                    return reject(this.errors);
                }, () => {
                    this.getCompletedBookings();
                    this.getUpcomingBookings().then(() => {
                        this.events.publish('booking:badge', this.upcomingBookings);
                        
                        this.getCurrentOrNextBooking().then(() => {
                        });
                    });
                });

        });

    }

    getBookingTimer() {
        return new Promise((resolve, reject) => {
            this.http.get(`/booking/${this.currentOrNextBooking.id}/timer`)
                .subscribe((booking: any) => {
                    return resolve(booking);
                }, error => {
                    this.errors = this.apiErrorHandler.handleError(error);
                    return reject(this.errors);
                }, () => {
                });

        });
    }

    getCurrentOrNextBooking() {

        return new Promise((resolve, reject) => {
            this.currentOrNextBooking = null;

            // First we'll check that there is actually a currentOrNext booking
            let currentOrNextBooking = this.upcomingBookings.length > 0 ? this.upcomingBookings[0] : null;

            // Next we need to check the booking is currently in the queue i.e. it has an estimated_booking_time set
            if (currentOrNextBooking && currentOrNextBooking.estimated_booking_time !== null && !currentOrNextBooking.is_advanced_booking) {
                // We'll set the current or next booking item and then remove this from the upcoming bookings list
                this.currentOrNextBooking = currentOrNextBooking;
                this.timeInSeconds = this.currentOrNextBooking.estimated_wait_time_in_seconds;
                this.upcomingBookings.shift();

                resolve(true)
            }
        })
    }

    getUpcomingBookings() {

        return new Promise(resolve => {

            this.upcomingBookings = [];
            for (let booking of this.bookings) {
                
                if (booking.completed_at === null && booking.cancelled_at === null) {
                    // If the booking is an advanced booking and doesn't have an estimated_booking_time set
                    // we'll use the requested_booking_time instead to force the upcoming bookings order
                    if (booking.type === 2 && booking.estimated_booking_time === null) {
                        // Because we set the estimated_booking_time to force the upcoming booking order
                        // we need to add a flag is_advanced_booking so this booking doesn't incorrectly
                        // get added as the currentOrNextBooking
                        booking.is_advanced_booking = true;
                        booking.estimated_booking_time = booking.requested_booking_time;
                        booking.estimated_booking_date_string = booking.requested_booking_date_string;
                    }
                    this.upcomingBookings.push(booking);
                }
            }

            this.sortUpcomingBookings();

            return resolve();

        });

    }

    getCompletedBookings() {
        this.completedBookings = [];
        for (let booking of this.bookings) {
            if (booking.completed_at !== null || booking.cancelled_at !== null) {

                // Handle a cancelled advanced booking date
                if (booking.estimated_booking_date_string === null) {
                    booking.estimated_booking_date_string = booking.requested_booking_date_string;
                }

                this.completedBookings.push(booking);
            }
        }

        this.sortCompletedBookings();

    }

    sortCompletedBookings() {
        this.completedBookings.sort((a, b) => a.estimated_booking_time >= b.estimated_booking_time ? -1 : 1);
    }

    sortUpcomingBookings() {
        this.upcomingBookings.sort((a, b) => a.estimated_booking_time <= b.estimated_booking_time ? -1 : 1);
    }

    getTotalCost(booking) {
      return this.bookingService.getTotalCost(booking);
    }

    getApproxServiceTime(services) {
       return this.bookingService.getTotalBookingLength(services);
    }

    // removeCancelledBooking(booking) {
    //
    //     // First try to remove the booking from the upcoming bookings array
    //     for (let i = 0; i < this.upcomingBookings.length; i++) {
    //         if (booking.id === this.upcomingBookings[i].id) {
    //             // Take the booking that has been removed and push it onto completed bookings
    //             this.completedBookings.push(this.upcomingBookings[i]);
    //             // Sort the bookings again since the time order will be different being pushed straight to the end of the array
    //             this.sortCompletedBookings();
    //             // Remove the booking from upcoming bookings
    //             this.upcomingBookings.splice(i, 1);
    //             // If we find a result stop iterating
    //             break;
    //         }
    //     }
    //
    //     // If we don't find the booking in the upcomingBookings then the cancelled booking is the currentOrNextBooking
    //     if (this.currentOrNextBooking && this.currentOrNextBooking.id === booking.id) {
    //         this.completedBookings.push(this.currentOrNextBooking);
    //         this.sortCompletedBookings();
    //         this.currentOrNextBooking = null;
    //     }
    //
    // }

    pullToRefresh() {
        this.getBookings().then(() => {
            this.refresher.complete();
        }).catch(() => {
            this.refresher.complete();
        });
    }

    findABarbershop() {
        let tabs: Tabs = this.navCtrl.parent;
        tabs.select(0);
    }

    setTimeInMinutes(seconds: number) {

      if (seconds < 0) return 0;

      let minutes = Math.ceil(seconds / 60);

      this.timeInMinutes = minutes;

      return minutes;

    }

    public openRescheduleBookingModal(booking) : void{
        this.bookingService.reScheduleBooking(booking).then(booking=>{
          // this.booking = booking;
        });
      }

}



// WEBPACK FOOTER //
// ./src/pages/bookings/bookings.ts